import { site } from './site'

export type LegalSection = { heading: string; paragraphs: string[] }

export type LegalDoc = {
  title: string
  updated: string
  intro: string
  sections: LegalSection[]
}

/** What this site actually stores and loads. Keep in sync with src/lib/storage.ts and src/lib/analytics.ts. */
export const privacyPolicy: LegalDoc = {
  title: 'Privacy Policy',
  updated: 'October 3, 2026',
  intro: `This policy covers ${site.url.replace('https://', '')}, the portfolio of ${site.name} trading as ${site.brand}.`,
  sections: [
    {
      heading: 'What this site collects',
      paragraphs: [
        'Nothing that identifies you. There is no account and no advertising or tracking cookie. The contact form sends nothing to this site: it opens your own mail app with your message laid out, and you choose whether to send it.',
        'If you email me, your message lives in my inbox and nowhere else. I keep it while we are working together and delete it on request.',
      ],
    },
    {
      heading: 'What your browser stores',
      paragraphs: [
        'Two small settings are saved in your own browser: your light or dark theme, and whether the sidebar is collapsed. They never leave your device and you can clear them any time from your browser settings.',
      ],
    },
    {
      heading: 'Analytics',
      paragraphs: [
        'No analytics runs today. If I add privacy-friendly page-view analytics later, it will only load after you agree to it, and this page will name the provider.',
      ],
    },
    {
      heading: 'Hosting',
      paragraphs: [
        'The site is served from my own server. Like every web server, it keeps standard access logs (IP address, page requested, time) for security and troubleshooting.',
      ],
    },
    {
      heading: 'Your rights',
      paragraphs: [
        `You can ask what I hold about you, or ask me to delete it. Email ${site.email} with the subject "Privacy request".`,
      ],
    },
    {
      heading: 'Changes',
      paragraphs: ['If this policy changes, the date at the top changes with it.'],
    },
  ],
}

export const termsOfService: LegalDoc = {
  title: 'Terms of Service',
  updated: 'October 1, 2026',
  intro: `These terms cover your use of this portfolio site, run by ${site.name} trading as ${site.brand}.`,
  sections: [
    {
      heading: 'Using this site',
      paragraphs: [
        'You can browse, read, and open every demo here for free. The demo pages are samples: the businesses, names, phone numbers, and emails inside them are made up for illustration.',
      ],
    },
    {
      heading: 'Working together',
      paragraphs: [
        'Nothing on this site is a binding offer. Client work starts with a discovery call, then a written proposal with scope and price. Only a signed agreement creates a contract.',
      ],
    },
    {
      heading: 'Intellectual property',
      paragraphs: [
        'The site design, copy, and my own builds belong to me. Third-party logos are shown to name the tools I work with, not to claim an endorsement. Client work appears with permission or with names removed.',
      ],
    },
    {
      heading: 'No warranties',
      paragraphs: [
        'The site is provided as is. I work to keep it accurate and online, but I cannot promise it is error-free or always available.',
      ],
    },
    {
      heading: 'External links',
      paragraphs: ['Links to other sites are for convenience. I am not responsible for their content or their privacy practices.'],
    },
    {
      heading: 'Governing law',
      paragraphs: ['These terms are governed by the laws of the Republic of the Philippines.'],
    },
    {
      heading: 'Contact',
      paragraphs: [`Questions about these terms: ${site.email}.`],
    },
  ],
}
